// TripticNav — premium landing navbar | architectural triptych SVG logo, Overview bordered capsule + portal tabs (Students / Counselors / Institutions) that anchor-scroll to their cards, frosted-glass on scroll, hamburger drawer on mobile

import { useState, useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { X, Menu } from "lucide-react";
import { CALENDLY_URL } from "../../constants";
// X kept for mobile close button

type PortalKey = "student" | "counselor" | "college";

interface TripticNavProps {
  onSignIn: () => void;
  onDashboard: () => void;
  isLoggedIn: boolean;
  onPortalSelect?: (portal: PortalKey) => void;
}

// Triptych logo mark — three gradient rounded bars, short | tall | medium
// Div-based for richer gradient + glow than SVG fill allows
function TripticMark() {
  return (
    <div className="flex items-end gap-[3px]" aria-hidden="true">
      <div className="w-[5px] h-[14px] rounded-full bg-gradient-to-t from-violet-600 via-violet-500 to-violet-300 shadow-[0_0_8px_rgba(139,92,246,0.6)]" />
      <div className="w-[5px] h-[22px] rounded-full bg-gradient-to-t from-violet-600 via-violet-400 to-violet-200 shadow-[0_0_10px_rgba(139,92,246,0.7)]" />
      <div className="w-[5px] h-[16px] rounded-full bg-gradient-to-t from-violet-600 via-violet-500 to-violet-300 shadow-[0_0_8px_rgba(139,92,246,0.6)]" />
    </div>
  );
}

type TabId = "overview" | "students" | "counselors" | "institutions";

// Flags kept from main so the tabs can be hidden again without code changes.
// The landing nav ships them visible; main had them all false.
const SHOW_OVERVIEW_TAB = true;
const SHOW_GET_STARTED = true;

function TripticNav({ onSignIn, onDashboard, isLoggedIn, onPortalSelect }: TripticNavProps) {
  const prefersReduced = useReducedMotion();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("overview");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleOverviewClick = () => {
    setActiveTab("overview");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Nav tabs scroll to the whole "Built for every side" section — heading plus
  // all three cards land in a balanced viewport — then flag which card was asked
  // for so PortalShowcase can emphasize it briefly.
  const scrollToPortals = (tabId: TabId, portal: PortalKey) => {
    setActiveTab(tabId);

    // Centre the composition (heading → cards → tagline) rather than the
    // section, whose large vertical padding would count toward centering and
    // leave the cards half-cut below the fold.
    const target =
      document.getElementById("portal-composition") ||
      document.getElementById("portal-showcase");

    target?.scrollIntoView({
      behavior: prefersReduced ? "auto" : "smooth",
      block: "center",
    });
    onPortalSelect?.(portal);
  };

  const handleStudentsClick = () => scrollToPortals("students", "student");
  const handleCounselorsClick = () => scrollToPortals("counselors", "counselor");
  const handleInstitutionsClick = () => scrollToPortals("institutions", "college");

  const isOverviewActive = activeTab === "overview";

  return (
    <>
      {/* ── Navbar bar ── */}
      <motion.nav
        className={[
          "landing-nav fixed top-0 left-0 right-0 z-50 transition-[background,border-color,backdrop-filter] duration-300",
          scrolled || mobileOpen
            ? "bg-[#09070f]/90 backdrop-blur-md border-b border-[rgba(106,74,170,0.18)]"
            : "bg-transparent border-b border-transparent",
        ].join(" ")}
        initial={prefersReduced ? { opacity: 1 } : { opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 h-[64px] flex items-center justify-between relative">

          {/* Logo — architectural mark + refined sans wordmark */}
          <a
            href="/"
            className="flex items-center gap-2 select-none"
            aria-label="Triptic home"
            onClick={() => setActiveTab("overview")}
          >
            <TripticMark />
            <motion.span
              className="font-sans leading-none"
              style={{ color: "#F8F7FF", fontSize: "21px", fontWeight: 600, letterSpacing: "0.07em", WebkitFontSmoothing: "antialiased" }}
              whileHover={{ color: "#EDE9FE", filter: "drop-shadow(0 0 10px rgba(139,92,246,0.45))" }}
              transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
            >
              triptic
            </motion.span>
          </a>

          {/* Center — Overview pill + portal tabs pill — absolutely centered in nav */}
          <div className="hidden md:flex items-center gap-3 absolute left-1/2 -translate-x-1/2">

            {/* Overview — bordered capsule with inner glow when active, purple pill on hover */}
            {SHOW_OVERVIEW_TAB && (
            <motion.button
              onClick={handleOverviewClick}
              className="relative font-sans text-sm"
              style={isOverviewActive ? {
                color: "#ffffff",
                fontWeight: 600,
                letterSpacing: "0.01em",
                WebkitFontSmoothing: "antialiased",
                background: "linear-gradient(160deg, rgba(109,40,217,0.72) 0%, rgba(55,10,148,0.88) 100%)",
                border: "1px solid rgba(168,85,247,0.42)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.28), 0 0 0 1px rgba(168,85,247,0.22), 0 8px 28px rgba(109,40,217,0.28)",
                padding: "7px 20px",
                borderRadius: "20px",
              } : {
                color: "rgba(255,255,255,0.45)",
                fontWeight: 400,
                letterSpacing: "0.02em",
                WebkitFontSmoothing: "antialiased",
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.07)",
                padding: "7px 20px",
                borderRadius: "20px",
              }}
              whileHover={!isOverviewActive && !prefersReduced ? {
                background: "linear-gradient(160deg, rgba(109,40,217,0.38) 0%, rgba(55,10,148,0.50) 100%)",
                color: "#ffffff",
                y: -1,
                scale: 1.02,
              } : {}}
              whileTap={!isOverviewActive && !prefersReduced ? { scale: 0.98, y: 0 } : {}}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              aria-current={isOverviewActive ? "page" : undefined}
            >
              Overview
            </motion.button>
            )}

            {/* Portal tabs — deep black pill with sliding 3D purple active tab */}
            <div
              className="flex items-center rounded-full px-1.5 py-1"
              style={{
                background: "rgba(5,3,15,0.85)",
                border: "1px solid rgba(255,255,255,0.07)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 4px 24px rgba(0,0,0,0.5)",
                overflow: "visible",
                gap: "2px",
              }}
            >
              <PortalTab
                label="Students"
                tabId="students"
                activeTab={activeTab}
                prefersReduced={!!prefersReduced}
                onClick={handleStudentsClick}
              />
              <PortalTab
                label="Counselors"
                tabId="counselors"
                activeTab={activeTab}
                prefersReduced={!!prefersReduced}
                onClick={handleCounselorsClick}
              />
              <PortalTab
                label="Institutions"
                tabId="institutions"
                activeTab={activeTab}
                prefersReduced={!!prefersReduced}
                onClick={handleInstitutionsClick}
              />
            </div>
          </div>

          {/* Right actions — desktop */}
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <button onClick={onDashboard} className="landing-ghost px-5 py-2 text-sm">
                Dashboard
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onSignIn}
                  className="landing-ghost-nav text-sm px-4 py-1.5"
                >
                  Sign In
                </button>
                {SHOW_GET_STARTED && (
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="landing-cta text-sm px-5 py-2"
                    style={{ textDecoration: 'none' }}
                  >
                    Book a Demo →
                  </a>
                )}
              </>
            )}
          </div>

          {/* Mobile: auth + hamburger */}
          <div className="flex md:hidden items-center gap-2">
            {!isLoggedIn && SHOW_GET_STARTED && (
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-cta px-4 py-2"
                style={{ fontSize: 13, textDecoration: 'none' }}
              >
                Book a Demo →
              </a>
            )}
            {isLoggedIn && (
              <button onClick={onDashboard} className="landing-ghost text-xs px-4 py-2">
                Dashboard
              </button>
            )}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="p-2 rounded-lg text-gray-400 hover:text-[#F8F7FF] hover:bg-[rgba(255,255,255,0.06)] transition-all duration-150"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* ── Mobile drawer ── */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              key="mobile-menu"
              className="md:hidden overflow-hidden"
              initial={prefersReduced ? { opacity: 1 } : { height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="px-4 pb-5 pt-2 flex flex-col gap-1 border-t border-[rgba(255,255,255,0.06)]">
                {SHOW_OVERVIEW_TAB && (
                  <MobileNavItem label="Overview" onClick={() => { setMobileOpen(false); handleOverviewClick(); }} />
                )}
                <MobileNavItem label="Students" onClick={() => { setMobileOpen(false); handleStudentsClick(); }} />
                <MobileNavItem label="Counselors" onClick={() => { setMobileOpen(false); handleCounselorsClick(); }} />
                <MobileNavItem label="Institutions" onClick={() => { setMobileOpen(false); handleInstitutionsClick(); }} />

                <div className="h-px bg-[rgba(255,255,255,0.06)] my-1" />

                {!isLoggedIn && (
                  <button
                    onClick={() => { setMobileOpen(false); onSignIn(); }}
                    className="w-full text-left px-4 py-3 rounded-xl font-sans text-sm text-gray-400 hover:text-[#F8F7FF] hover:bg-[rgba(255,255,255,0.05)] transition-all duration-150"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

    </>
  );
}

// ─── PortalTab — sliding background pill via layoutId ────────────────────────

interface PortalTabProps {
  label: string;
  tabId: TabId;
  activeTab: TabId;
  prefersReduced: boolean;
  onClick: () => void;
}

function PortalTab({ label, tabId, activeTab, prefersReduced, onClick }: PortalTabProps) {
  const isActive = activeTab === tabId;
  const [isHovering, setIsHovering] = useState(false);
  return (
    <motion.button
      onClick={onClick}
      className="relative font-sans text-sm"
      style={{
        color: isActive || isHovering ? "#ffffff" : "rgba(255,255,255,0.45)",
        fontWeight: isActive ? 600 : 400,
        letterSpacing: isActive ? "0.01em" : "0.02em",
        WebkitFontSmoothing: "antialiased",
        padding: "7px 16px",
        borderRadius: "20px",
        background: "transparent",
        border: "none",
        cursor: "pointer",
      }}
      onHoverStart={() => { if (!isActive) setIsHovering(true); }}
      onHoverEnd={() => setIsHovering(false)}
      whileHover={!isActive && !prefersReduced ? { y: -1, scale: 1.02 } : {}}
      whileTap={!isActive && !prefersReduced ? { scale: 0.98, y: 0 } : {}}
      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
    >
      {/* Sliding background pill — premium 3D gradient when active */}
      {isActive && (
        <motion.span
          layoutId="active-tab"
          className="absolute inset-0 rounded-[20px]"
          style={{
            background: "linear-gradient(160deg, rgba(109,40,217,0.72) 0%, rgba(55,10,148,0.88) 100%)",
            border: "1px solid rgba(168,85,247,0.42)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.20), inset 0 -1px 0 rgba(0,0,0,0.30), 0 0 0 1px rgba(168,85,247,0.22), 0 8px 28px rgba(109,40,217,0.32), 0 2px 8px rgba(0,0,0,0.42)",
          }}
          transition={
            prefersReduced
              ? { duration: 0 }
              : { type: "spring", stiffness: 400, damping: 30 }
          }
        />
      )}
      {/* Hover pill — lighter version, fades in/out, no layoutId conflict */}
      <AnimatePresence>
        {!isActive && isHovering && !prefersReduced && (
          <motion.span
            className="absolute inset-0 rounded-[20px]"
            style={{
              background: "linear-gradient(160deg, rgba(109,40,217,0.35) 0%, rgba(55,10,148,0.50) 100%)",
              border: "1px solid rgba(168,85,247,0.22)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 0 0 1px rgba(168,85,247,0.14), 0 4px 16px rgba(109,40,217,0.18)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
      </AnimatePresence>
      <span className="relative z-10">{label}</span>
    </motion.button>
  );
}

// ─── MobileNavItem ────────────────────────────────────────────────────────────

interface MobileNavItemProps {
  label: string;
  href?: string;
  onClick: () => void;
}

function MobileNavItem({ label, href, onClick }: MobileNavItemProps) {
  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className="px-4 py-3 rounded-xl font-sans text-sm text-gray-400 hover:text-[#F8F7FF] hover:bg-[rgba(255,255,255,0.05)] transition-all duration-150"
      >
        {label}
      </a>
    );
  }
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 rounded-xl font-sans text-sm text-gray-400 hover:text-[#F8F7FF] hover:bg-[rgba(255,255,255,0.05)] transition-all duration-150"
    >
      {label}
    </button>
  );
}

export default TripticNav;
